import cn from 'classnames'
import {
  checkCompanyEnglishName,
  checkCompanyLegalName,
} from 'legacy/lut/helpers/validateRules/partner'
import { useValidation } from 'legacy/lut/hooks/useValidation'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { PartnerFields, PartnerHierarchyType } from 'clickNgo/entities/partner/configs'
import { asianCountriesWithEspeciallyRequirements } from 'clickNgo/entities/partner/configs/common'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { FormCheckBox, FormInput, useGetFormDataField } from 'cng-common/features/form'
import { FieldWrapperWithChanges } from 'cng-common/features/form/components'
import { wasFieldModified } from 'cng-common/features/form/helpers'
import { FieldGroup } from 'shared/components/fieldGroup'

import { partnerLegalInfoTranslation } from '../../translation'

import { ILegalCommonBlockProps } from './models'

import styles from './LegalCommonBlock.module.scss'

export const LegalCommonBlock = memo<ILegalCommonBlockProps>(
  ({
    formKeys,
    isDisabled,
    isReadOnly,
    isNewPartner,
    hasEntryId,
    companyEnglishName,
    companyLegalName,
    legalModifiedValues,
  }) => {
    /** -------------------- Variables ----------------------- */

    const [isCompanyLegalNameDisabled, setIsCompanyLegalNameDisabled] = useState(false)
    const [isCompanyEnglishNameDisabled, setIsCompanyEnglishNameDisabled] = useState(false)

    const [, handleCompanyEnglishNameChange, isCompanyEnglishNameModified] =
      useGetFormDataField<string>(formKeys.legal, PartnerFields.companyEnglishName.field)
    const [, handleCompanyLegalNameChange] = useGetFormDataField<string>(
      formKeys.legal,
      PartnerFields.companyLegalName.field
    )
    const [communicationName, , isCommunicationNameModified] = useGetFormDataField<string>(
      formKeys.basic,
      PartnerFields.communicationName.field
    )
    const [hierarchyType] = useGetFormDataField<PartnerHierarchyType>(
      formKeys.basic,
      PartnerFields.hierarchyType.field
    )

    const [legalCountry] = useGetFormDataField<PartnerHierarchyType>(
      formKeys.legal,
      PartnerFields.legalCountry.field
    )

    const [country] = useGetFormDataField<PartnerHierarchyType>(
      formKeys.basic,
      PartnerFields.country.field
    )

    const isCompanyEnglishNameVisible = useMemo(() => {
      if (legalCountry) {
        return asianCountriesWithEspeciallyRequirements.includes(legalCountry)
      }

      return asianCountriesWithEspeciallyRequirements.includes(country)
    }, [country, legalCountry])

    const onCompanyEnglishNameValidate = useValidation({
      rules: checkCompanyEnglishName,
      isPassed: !isNewPartner && !isCompanyEnglishNameModified,
    })

    const onCompanyLegalNameValidate = useValidation({
      rules: checkCompanyLegalName,
      isPassed: !isNewPartner && !isCompanyEnglishNameModified,
    })

    const isCompanyEnglishNameRequired = useMemo(
      () =>
        isCompanyEnglishNameVisible &&
        !setIsCompanyEnglishNameDisabled &&
        (isNewPartner || isCompanyEnglishNameModified),
      [isNewPartner, isCompanyEnglishNameModified, isCompanyEnglishNameVisible]
    )

    const isDoCopyBasicNameDisabled = isDisabled || !isCommunicationNameModified || hasEntryId

    /** -------------------- Handlers ----------------------- */

    const handleOnDoCopyBasicNameChange = useCallback(
      async (doCopyBasicName: boolean) => {
        if (doCopyBasicName) {
          const isNotValid = await onCompanyEnglishNameValidate(communicationName)

          if (isNotValid) {
            handleCompanyLegalNameChange(communicationName, true)
            setIsCompanyLegalNameDisabled(true)
          } else {
            if (isCompanyEnglishNameVisible && !isCompanyEnglishNameDisabled) {
              handleCompanyEnglishNameChange(communicationName, true)
              setIsCompanyEnglishNameDisabled(true)
            }
            handleCompanyLegalNameChange(communicationName, true)
            setIsCompanyLegalNameDisabled(true)
          }
        } else {
          setIsCompanyLegalNameDisabled(false)
          setIsCompanyEnglishNameDisabled(false)
        }
      },
      [
        communicationName,
        handleCompanyEnglishNameChange,
        handleCompanyLegalNameChange,
        isCompanyEnglishNameDisabled,
        isCompanyEnglishNameVisible,
        onCompanyEnglishNameValidate,
      ]
    )

    /** -------------------- Effects ----------------------- */

    useEffect(() => {
      setIsCompanyEnglishNameDisabled(
        [PartnerHierarchyType.AFFILIATE, PartnerHierarchyType.RESELLER_NETWORK].includes(
          hierarchyType
        ) && isCompanyEnglishNameVisible
      )
    }, [hierarchyType, isCompanyEnglishNameVisible])

    return (
      <FieldGroup title={partnerLegalInfoTranslation.content.blockTitles.legalCommonBlock}>
        <RenderWhen condition={isCompanyEnglishNameVisible && !isReadOnly}>
          <div className={cn(styles.fullWidth)}>
            <InfoBlock
              text={partnerLegalInfoTranslation.content.notification.companyLegalName}
              type={InfoBlockType.Help}
            />
          </div>
        </RenderWhen>

        <div className={cn({ [styles.hidden]: hasEntryId }, styles.fullWidth)}>
          <FormCheckBox
            field={PartnerFields.doCopyBasicName.field}
            label={PartnerFields.doCopyBasicName.label}
            defaultValue={false}
            formKey={formKeys.settings}
            onChange={handleOnDoCopyBasicNameChange}
            isDisabled={isDoCopyBasicNameDisabled}
            isReadOnly={isReadOnly}
          />
        </div>
        <RenderWhen condition={isCompanyEnglishNameVisible}>
          <FieldWrapperWithChanges
            hasChanges={wasFieldModified(
              legalModifiedValues,
              PartnerFields.companyEnglishName.externalField
            )}
          >
            <FormInput
              formKey={formKeys.legal}
              field={PartnerFields.companyEnglishName.field}
              label={PartnerFields.companyEnglishName.label}
              defaultValue={companyEnglishName}
              onValidate={onCompanyEnglishNameValidate}
              isDisabled={isDisabled || isCompanyEnglishNameDisabled}
              isReadOnly={isReadOnly}
              isRequired={isCompanyEnglishNameRequired}
            />
          </FieldWrapperWithChanges>
        </RenderWhen>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            legalModifiedValues,
            PartnerFields.companyLegalName.externalField
          )}
        >
          <FormInput
            formKey={formKeys.legal}
            field={PartnerFields.companyLegalName.field}
            label={PartnerFields.companyLegalName.label}
            defaultValue={companyLegalName}
            onValidate={onCompanyLegalNameValidate}
            isDisabled={isDisabled || isCompanyLegalNameDisabled}
            isReadOnly={isReadOnly}
            isRequired
          />
        </FieldWrapperWithChanges>
      </FieldGroup>
    )
  }
)
